// src/pages/stock/Codes.tsx
//
// Codes (Stock Codes)
// - Uses standard table UI (StandardTableToolbar / StandardTableFrame / StandardPagination)
// - URL is the single source of truth for applied filters and page state
// - Separates draft search state from applied search state
// - Supports export via existing search panel
// - Provides inventory risk-based row highlighting
// - Header actions are managed via AppPageHeader slots (Add Stock Code)

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  CheckCircleOutline,
  ErrorOutline,
  PauseCircleOutline,
  ReportProblemOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { utcToZonedTime } from 'date-fns-tz';

import {
  StockOrder,
  type GetStocksParams,
  type StockResponse,
  exportStocksExcel,
} from '@apis/stock';
import { StockStatus } from '@models/stock';
import {
  getStockStatusLabel,
} from '@constants/stockLabels';
import {
  INVENTORY_COVERAGE_BASIS_TOOLTIP,
  INVENTORY_COVERAGE_SHIPPED_WINDOW_DAYS,
  getInventoryCoverageLevel,
} from '@constants/inventoryCoverage';

import CodesSearchPannel from '@components/stock/CodesSearchPanel';
import type { SetSearchValueFn } from '@components/common/search';

import { useRegionList } from '@queries/regions/useRegionList';
import { useStockCount } from '@queries/stocks/useStockCount';
import { useStockList } from '@queries/stocks/useStockList';
import { useAllSupplierList } from '@queries/suppliers/useAllSupplierList';

import { KOREAN_TIME_ZONE } from '@constants/timezone';
import { downloadBlob } from '@utils/file';
import { filterSearchParams } from '@utils/search';

import useAsyncEffect from '@hooks/useAsyncEffect';
import { useListReturnNavigation } from '@hooks/useListReturnNavigation';
import { usePageHeader } from '@components/common/pageHeader.context';

import {
  StandardPagination,
  StandardTableFrame,
  StandardTableToolbar,
  cellSx,
} from '@components/common/table';

const PAGE_SIZE = 50;
const PAGE_AUTO_SEARCH = { enabled: false, delayMs: 500 } as const;

type SearchForm = GetStocksParams;
type Option<T> = { label: string; value: T };

const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));

const parseNumberParam = (value: string | null): number | undefined => {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const normalizeText = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

const isValidEnumValue = <T extends string>(
  values: readonly string[],
  v: string | null,
): v is T => {
  if (!v) return false;
  return values.includes(v);
};

/**
 * Reads search parameters from URL and converts them into a SearchForm.
 * This represents the applied (server-side) search state.
 */
const readSearchFromUrl = (sp: URLSearchParams): SearchForm => {
  const statusRaw = sp.get('status');
  const orderingRaw = sp.get('ordering');

  const status = isValidEnumValue<StockStatus>(
    Object.values(StockStatus) as unknown as string[],
    statusRaw,
  )
    ? (statusRaw as StockStatus)
    : undefined;

  const ordering = isValidEnumValue<StockOrder>(
    Object.values(StockOrder) as unknown as string[],
    orderingRaw,
  )
    ? (orderingRaw as StockOrder)
    : undefined;

  return {
    stockCode: sp.get('stockCode') || '',
    stockName: sp.get('stockName') || '',
    regionId: parseNumberParam(sp.get('regionId')),
    supplierId: parseNumberParam(sp.get('supplierId')),
    status,
    ordering,
  };
};

/**
 * Normalizes search parameters before applying them to API calls or URL.
 */
const cleanSearchForm = (form: SearchForm): SearchForm => {
  const stockCode = normalizeText(form.stockCode);
  const stockName = normalizeText(form.stockName);

  return {
    stockCode,
    stockName,
    regionId: typeof form.regionId === 'number' ? form.regionId : undefined,
    supplierId: typeof form.supplierId === 'number' ? form.supplierId : undefined,
    status: form.status || undefined,
    ordering: form.ordering || undefined,
  };
};

const buildUrlParams = (form: SearchForm, page: number) => {
  const cleaned = cleanSearchForm(form);

  const params: Record<string, string> = {};
  if (cleaned.stockCode) params.stockCode = cleaned.stockCode;
  if (cleaned.stockName) params.stockName = cleaned.stockName;
  if (typeof cleaned.regionId === 'number') params.regionId = String(cleaned.regionId);
  if (typeof cleaned.supplierId === 'number') params.supplierId = String(cleaned.supplierId);
  if (cleaned.status) params.status = String(cleaned.status);
  if (cleaned.ordering) params.ordering = String(cleaned.ordering);

  return {
    ...filterSearchParams(params),
    page: String(page),
  };
};

const formatNumber = (n: unknown) => {
  const v = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(v)) return '-';
  return v.toLocaleString();
};

const toSafeNumber = (v: unknown, fallback = 0) => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};

type StockRiskLevel = 'normal' | 'warning' | 'error';
type StockRisk = { level: StockRiskLevel; coverageDays: number; avgDaily: number };
type CoverageDisplayLevel = StockRiskLevel | 'noSales' | 'inactive' | 'unmanaged';

/**
 * Calculates inventory risk based on remaining stock and last-week shipment volume.
 */
const calcStockRisk = (
  remainedCount: unknown,
  lastWeekShippedCount: unknown,
): StockRisk => {
  const remained = toSafeNumber(remainedCount, 0);
  const shippedWeek = toSafeNumber(lastWeekShippedCount, 0);

  if (remained <= 0) {
    return {
      level: 'error',
      coverageDays: 0,
      avgDaily: shippedWeek > 0 ? shippedWeek / INVENTORY_COVERAGE_SHIPPED_WINDOW_DAYS : 0,
    };
  }

  if (shippedWeek <= 0) {
    return { level: 'normal', coverageDays: Infinity, avgDaily: 0 };
  }

  const avgDaily = shippedWeek / INVENTORY_COVERAGE_SHIPPED_WINDOW_DAYS;
  const coverageDays = avgDaily > 0 ? remained / avgDaily : Infinity;
  const coverageLevel = getInventoryCoverageLevel(coverageDays);

  if (coverageLevel === 'critical') return { level: 'error', coverageDays, avgDaily };
  if (coverageLevel === 'warning') return { level: 'warning', coverageDays, avgDaily };
  return { level: 'normal', coverageDays, avgDaily };
};

const formatCoverage = (coverageDays: number) => {
  if (!Number.isFinite(coverageDays)) return '-';
  if (coverageDays <= 0) return '0';
  return coverageDays.toFixed(1);
};

const getCoverageDisplay = (
  row: StockResponse,
  risk: StockRisk,
): {
  level: CoverageDisplayLevel;
  label: string;
  color: string;
  icon: React.ReactNode | null;
  tooltip: React.ReactNode;
} => {
  if (row.status === StockStatus.INACTIVE) {
    return {
      level: 'inactive',
      label: '',
      color: 'text.disabled',
      icon: <PauseCircleOutline fontSize="small" />,
      tooltip: 'Inactive stock code',
    };
  }

  if (!row.isInventoryManaged) {
    return {
      level: 'unmanaged',
      label: '-',
      color: 'text.disabled',
      icon: null,
      tooltip: 'Inventory is not managed for this stock code.',
    };
  }

  const remained = toSafeNumber(row.remainedCount, 0);
  const shippedWeek = toSafeNumber(row.lastWeekShippedCount, 0);

  if (remained <= 0) {
    return {
      level: 'error',
      label: '0 d',
      color: 'error.main',
      icon: <ErrorOutline fontSize="small" />,
      tooltip: (
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 800, display: 'block' }}>
            No remaining stock
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            Remained: 0
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            Shipped (7d): {formatNumber(shippedWeek)}
          </Typography>
        </Box>
      ),
    };
  }

  if (shippedWeek <= 0) {
    return {
      level: 'noSales',
      label: 'No 7d sales',
      color: 'text.disabled',
      icon: null,
      tooltip: (
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 800, display: 'block' }}>
            No recent sales
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            No shipped quantity in the last 7 days, excluding today.
          </Typography>
        </Box>
      ),
    };
  }

  const coverageLabel = `${formatCoverage(risk.coverageDays)} d`;
  const isError = risk.level === 'error';
  const isWarning = risk.level === 'warning';

  return {
    level: risk.level,
    label: coverageLabel,
    color: isError ? 'error.main' : isWarning ? 'warning.main' : 'text.primary',
    icon: isError ? (
      <ReportProblemOutlined fontSize="small" />
    ) : isWarning ? (
      <WarningAmberOutlined fontSize="small" />
    ) : null,
    tooltip: (
      <Box>
        <Typography variant="caption" sx={{ fontWeight: 800, display: 'block' }}>
          Coverage: {coverageLabel}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>
          Avg/day: {risk.avgDaily ? risk.avgDaily.toFixed(2) : '0.00'}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>
          {INVENTORY_COVERAGE_BASIS_TOOLTIP}
        </Typography>
      </Box>
    ),
  };
};

const getStockStatusDisplay = (status?: StockStatus | string | null) => {
  switch (status) {
    case StockStatus.INACTIVE:
      return {
        label: 'Inactive',
        color: 'text.disabled',
        icon: <PauseCircleOutline fontSize="small" />,
      };
    case StockStatus.ACTIVE:
    default:
      return {
        label: 'Active',
        color: 'success.main',
        icon: <CheckCircleOutline fontSize="small" />,
      };
  }
};

const getRiskCellSx = (level: CoverageDisplayLevel, isInactive: boolean) => {
  if (isInactive) return { opacity: 0.68 };
  if (level === 'error') {
    return { backgroundColor: 'rgba(211, 47, 47, 0.06)' };
  }
  if (level === 'warning') {
    return { backgroundColor: 'rgba(237, 108, 2, 0.06)' };
  }
  return {};
};

const getCountSx = (
  kind: 'remained' | 'shipped' | 'total' | 'refunded' | 'expired',
  value: number,
  level: CoverageDisplayLevel,
  isInactive: boolean,
) => {
  const base = {
    ...cellSx.number,
    opacity: isInactive ? 0.68 : 1,
    fontWeight: 600,
  };

  if (isInactive || level === 'unmanaged') {
    return { ...base, color: 'text.secondary' };
  }

  if (kind === 'remained') {
    return {
      ...base,
      color: value <= 0 ? 'error.main' : 'text.primary',
      fontWeight: 800,
    };
  }

  if (kind === 'shipped' && value <= 0) {
    return { ...base, color: 'text.disabled' };
  }

  if (kind === 'refunded' && value > 0) {
    return { ...base, color: 'warning.main', fontWeight: 800 };
  }

  if (kind === 'expired' && value > 0) {
    return { ...base, color: 'error.main', fontWeight: 800 };
  }

  if (level === 'noSales' && kind === 'total') {
    return { ...base, color: 'text.secondary' };
  }

  return base;
};

/**
 * Shallow comparison for detecting meaningful differences between search forms.
 * Used to avoid overwriting draft state unnecessarily.
 */
const isSameSearchForm = (a: SearchForm, b: SearchForm) => {
  const aa = cleanSearchForm(a);
  const bb = cleanSearchForm(b);

  return (
    (aa.stockCode ?? '') === (bb.stockCode ?? '') &&
    (aa.stockName ?? '') === (bb.stockName ?? '') &&
    (aa.regionId ?? null) === (bb.regionId ?? null) &&
    (aa.supplierId ?? null) === (bb.supplierId ?? null) &&
    (aa.status ?? null) === (bb.status ?? null) &&
    (aa.ordering ?? null) === (bb.ordering ?? null)
  );
};

const Codes = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { navigateFromList } = useListReturnNavigation();
  const { setPageHeaderSlots } = usePageHeader();

  /**
   * appliedSearch:
   *   Represents the active filters synced with URL and API calls.
   * draftSearch:
   *   Represents the editable state inside the search panel UI.
   */
  const [appliedSearch, setAppliedSearch] = useState<SearchForm>(() =>
    readSearchFromUrl(searchParams),
  );
  const [draftSearch, setDraftSearch] = useState<SearchForm>(() => ({
    ...readSearchFromUrl(searchParams),
  }));

  const [page, setPage] = useState<number>(() => {
    const raw = searchParams.get('page');
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 1;
  });

  const [filtersOpen, setFiltersOpen] = useState(false);

  /**
   * Synchronizes component state from URL parameters.
   */
  useEffect(() => {
    const nextApplied = readSearchFromUrl(searchParams);

    const raw = searchParams.get('page');
    const n = Number(raw);
    const nextPage = Number.isFinite(n) && n > 0 ? n : 1;

    setAppliedSearch(nextApplied);
    setDraftSearch((prev) =>
      isSameSearchForm(prev, nextApplied) ? prev : nextApplied,
    );
    setPage(nextPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const cleanedApplied = useMemo(
    () => cleanSearchForm(appliedSearch),
    [appliedSearch],
  );

  // Queries
  const { data, isLoading } = useStockList({ page, searchParam: cleanedApplied });
  const { data: totalCount, isLoading: isLoadingCount } =
    useStockCount(cleanedApplied);

  const safeTotalCount = totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(safeTotalCount / PAGE_SIZE));
  const rows: StockResponse[] = (data?.data ?? []) as StockResponse[];

  const isTableLoading = Boolean(isLoading || isLoadingCount);

  // Options
  const { data: regionsData } = useRegionList();
  const { data: suppliersData } = useAllSupplierList();

  const [regionOptions, setRegionOptions] = useState<Array<Option<number>>>([]);
  const [supplierOptions, setSupplierOptions] = useState<Array<Option<number>>>([]);

  useAsyncEffect(async () => {
    if (!regionsData) return;
    setRegionOptions(
      (regionsData.data ?? []).map((r) => ({
        label: r.regionCode,
        value: r.regionId,
      })),
    );
  }, [regionsData]);

  useAsyncEffect(async () => {
    if (!suppliersData) return;
    setSupplierOptions(
      (suppliersData.data ?? []).map((s) => ({
        label: `${s.supplierName} (${s.supplierCode})`,
        value: s.supplierId,
      })),
    );
  }, [suppliersData]);

  const statusOptions = useMemo<Array<Option<StockStatus>>>(() => {
    const values = Object.values(StockStatus) as StockStatus[];
    return values.map((v) => ({ value: v, label: getStockStatusLabel(v) }));
  }, []);

  const selectedRegion = useMemo(() => {
    const id = draftSearch.regionId;
    if (typeof id !== 'number') return null;
    return regionOptions.find((o) => o.value === id) ?? null;
  }, [draftSearch.regionId, regionOptions]);

  const selectedSupplier = useMemo(() => {
    const id = draftSearch.supplierId;
    if (typeof id !== 'number') return null;
    return supplierOptions.find((o) => o.value === id) ?? null;
  }, [draftSearch.supplierId, supplierOptions]);

  const selectedStatus = useMemo(() => {
    const v = draftSearch.status as StockStatus | undefined;
    if (!v) return null;
    return statusOptions.find((o) => o.value === v) ?? null;
  }, [draftSearch.status, statusOptions]);

  /**
   * Updates draft search values from the shared search component.
   */
  const setValue: SetSearchValueFn<SearchForm> = useCallback((key, value) => {
    setDraftSearch((prev) => {
      const next = { ...prev } as Record<string, unknown>;

      switch (key as keyof SearchForm) {
        case 'stockCode':
        case 'stockName':
          next[key] = typeof value === 'string' ? value : '';
          break;

        case 'regionId':
        case 'supplierId':
          next[key] = typeof value === 'number' ? value : undefined;
          break;

        case 'status':
        case 'ordering':
          next[key] = value ?? undefined;
          break;

        default:
          next[key as string] = value;
          break;
      }

      return next as SearchForm;
    });
  }, []);

  // Apply / Reset / Pagination
  const applyFilters = useCallback(
    (nextDraft?: SearchForm) => {
      const next = cleanSearchForm(nextDraft ?? draftSearch);

      setAppliedSearch(next);
      setPage(1);
      setSearchParams(buildUrlParams(next, 1));
    },
    [draftSearch, setSearchParams],
  );

  const handleSearch = useCallback(() => applyFilters(), [applyFilters]);

  const handleReset = useCallback(() => {
    const cleared: SearchForm = {
      stockCode: '',
      stockName: '',
      regionId: undefined,
      supplierId: undefined,
      status: undefined,
      ordering: appliedSearch.ordering,
    };

    setDraftSearch(cleared);
    applyFilters(cleared);
  }, [appliedSearch.ordering, applyFilters]);

  const reorderRiskFirst = cleanedApplied.ordering === StockOrder.REORDER_REQUIRED;

  const handleToggleReorderRiskFirst = useCallback(
    (checked: boolean) => {
      const nextSearch: SearchForm = {
        ...appliedSearch,
        ordering: checked ? StockOrder.REORDER_REQUIRED : undefined,
      };

      setDraftSearch((prev) => ({
        ...prev,
        ordering: nextSearch.ordering,
      }));
      applyFilters(nextSearch);
    },
    [appliedSearch, applyFilters],
  );

  const handleChangePage = (next: number) => {
    const safeNext = clamp(next, 1, totalPages);
    setPage(safeNext);
    setSearchParams(buildUrlParams(appliedSearch, safeNext));
  };

  // Export
  const [isExporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (isExporting) return;

    setExporting(true);
    try {
      const nowUtc = new Date();
      const kstDate = utcToZonedTime(nowUtc, KOREAN_TIME_ZONE);

      const res = await exportStocksExcel(cleanedApplied);
      const fileName = `stock-codes-${kstDate.toISOString()}.xlsx`;
      downloadBlob(res, fileName);
    } finally {
      setExporting(false);
    }
  }, [cleanedApplied, isExporting]);

  // Navigation
  const handleOnAdd = useCallback(() => {
    navigate('/stocks/codes/new');
  }, [navigate]);

  const handleOpenEdit = useCallback(
    (stockId: number) => {
      navigateFromList(`/stocks/codes/${stockId}/edit`);
    },
    [navigateFromList],
  );

  /**
   * Registers page-level header actions.
   * Actions are explicitly cleared on unmount to avoid leakage.
   */
  useEffect(() => {
    setPageHeaderSlots({
      actions: [
        {
          key: 'add',
          label: 'Add Stock Code',
          startIcon: <AddIcon />,
          onClick: handleOnAdd,
          variant: 'contained',
          primary: true,
          disabled: isTableLoading,
        },
      ],
    });

    return () => setPageHeaderSlots({});
  }, [setPageHeaderSlots, handleOnAdd, isTableLoading]);

  const columnsCount = 11;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box mt={2}>
        <CodesSearchPannel
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          draftSearch={draftSearch}
          setValue={setValue}
          regionOptions={regionOptions}
          supplierOptions={supplierOptions}
          statusOptions={statusOptions}
          selectedRegion={selectedRegion}
          selectedSupplier={selectedSupplier}
          selectedStatus={selectedStatus}
          onSearch={handleSearch}
          onReset={handleReset}
          onExport={handleExport}
          exportLoading={isExporting}
          autoSearch={PAGE_AUTO_SEARCH}
        />
      </Box>

      <Box mt={1.5} display="flex" justifyContent="flex-end">
        <Tooltip title="Sort stock codes by low inventory coverage and remaining stock.">
          <FormControlLabel
            labelPlacement="start"
            control={
              <Switch
                checked={reorderRiskFirst}
                onChange={(e) => handleToggleReorderRiskFirst(e.target.checked)}
                size="small"
                color="warning"
              />
            }
            label={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <WarningAmberOutlined sx={{ fontSize: 16 }} />
                <Typography variant="caption" fontWeight={600}>
                  Reorder risk first
                </Typography>
              </Stack>
            }
            sx={(theme) => ({
              m: 0,
              color: reorderRiskFirst ? 'warning.main' : 'text.secondary',
              transition: theme.transitions.create(['color'], {
                duration: theme.transitions.duration.shortest,
              }),
              '& .MuiFormControlLabel-label': {
                display: 'flex',
              },
              '& .MuiSwitch-root': {
                ml: 0.5,
              },
            })}
          />
        </Tooltip>
      </Box>

      <Box mt={2} />
      <StandardTableToolbar
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={safeTotalCount}
        onChangePage={handleChangePage}
        showTotalCount
        showPageJump
      />

      {(isTableLoading || isExporting) && (
        <Box mt={1}>
          <LinearProgress />
        </Box>
      )}

      <Box mt={1}>
        <StandardTableFrame<StockResponse>
          containerSx={{ borderRadius: 2 }}
          minWidth={1300}
          columnsCount={columnsCount}
          isLoading={isTableLoading}
          loadingVariant="skeleton"
          skeletonRows={8}
          emptyText="No stock codes found."
          getRowStatus={(row) => {
            if (row.status === StockStatus.INACTIVE) return 'inactive';
            return 'normal';
          }}
          head={
            <TableRow>
              <TableCell width={80} align="right">#</TableCell>
              <TableCell sx={{ minWidth: 160 }}>Stock Code</TableCell>
              <TableCell sx={{ minWidth: 220 }}>Stock Name</TableCell>
              <TableCell sx={{ minWidth: 220 }}>Supplier</TableCell>
              <TableCell width={120}>Region</TableCell>
              <TableCell width={120} align="right">Remained</TableCell>
              <TableCell width={130} align="right">Coverage</TableCell>
              <TableCell width={140} align="right">Shipped (7d)</TableCell>
              <TableCell width={140} align="right">Shipped (Total)</TableCell>
              <TableCell width={120} align="right">Refunded</TableCell>
              <TableCell width={120} align="right">Expired</TableCell>
            </TableRow>
          }
          rows={rows}
          getRowKey={(row) => String(row.stockId)}
          renderRow={(row, index) => {
            const stockId = Number(row.stockId);
            const remained = toSafeNumber(row.remainedCount, 0);
            const shippedWeek = toSafeNumber(row.lastWeekShippedCount, 0);
            const shippedTotal = toSafeNumber(row.totalShippedCount, 0);
            const refunded = toSafeNumber(row.refundedCount, 0);
            const expired = toSafeNumber(row.expiredCount, 0);
            const isInactive = row.status === StockStatus.INACTIVE;
            const stockStatusMeta = getStockStatusDisplay(row.status);

            const risk = calcStockRisk(remained, shippedWeek);
            const coverageMeta = getCoverageDisplay(row, risk);
            const riskCellSx = getRiskCellSx(coverageMeta.level, isInactive);

            return (
              <>
                <TableCell
                  align="right"
                  sx={{ ...cellSx.number, opacity: isInactive ? 0.68 : 1 }}
                >
                  {(page - 1) * PAGE_SIZE + index + 1}
                </TableCell>

                <TableCell sx={{ ...cellSx.nowrap, opacity: isInactive ? 0.68 : 1 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box
                      component="span"
                      sx={{
                        color: stockStatusMeta.color,
                        display: 'inline-flex',
                        alignItems: 'center',
                        flexShrink: 0,
                      }}
                      aria-label={stockStatusMeta.label}
                      title={stockStatusMeta.label}
                    >
                      {stockStatusMeta.icon}
                    </Box>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => handleOpenEdit(stockId)}
                      sx={{
                        minWidth: 0,
                        px: 0,
                        fontWeight: 800,
                        textTransform: 'none',
                        textDecoration: 'underline',
                        textUnderlineOffset: '3px',
                      }}
                    >
                      {row.stockCode || '-'}
                    </Button>
                  </Stack>
                </TableCell>

                <TableCell sx={{ minWidth: 220, opacity: isInactive ? 0.68 : 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                    {row.stockName || '-'}
                  </Typography>
                </TableCell>

                <TableCell sx={{ minWidth: 220, opacity: isInactive ? 0.68 : 1 }}>
                  <Typography variant="body2" noWrap title={row.supplier?.supplierName ?? ''}>
                    {row.supplier?.supplierName ?? '-'}
                  </Typography>
                </TableCell>

                <TableCell sx={{ ...cellSx.nowrap, opacity: isInactive ? 0.68 : 1 }}>
                  {row.region?.regionCode ?? '-'}
                </TableCell>

                <TableCell align="right" sx={getCountSx('remained', remained, coverageMeta.level, isInactive)}>
                  {formatNumber(remained)}
                </TableCell>

                <TableCell
                  align="right"
                  sx={{ ...riskCellSx, ...cellSx.number, opacity: isInactive ? 0.68 : 1 }}
                >
                  <Tooltip title={coverageMeta.tooltip} arrow>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      justifyContent="flex-end"
                      sx={{
                        color: coverageMeta.color,
                        fontWeight: coverageMeta.level === 'normal' ? 700 : 800,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {coverageMeta.icon && (
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          {coverageMeta.icon}
                        </Box>
                      )}
                      {coverageMeta.label && <Box component="span">{coverageMeta.label}</Box>}
                    </Stack>
                  </Tooltip>
                </TableCell>

                <TableCell align="right" sx={getCountSx('shipped', shippedWeek, coverageMeta.level, isInactive)}>
                  {formatNumber(shippedWeek)}
                </TableCell>

                <TableCell align="right" sx={getCountSx('total', shippedTotal, coverageMeta.level, isInactive)}>
                  {formatNumber(shippedTotal)}
                </TableCell>

                <TableCell align="right" sx={getCountSx('refunded', refunded, coverageMeta.level, isInactive)}>
                  {formatNumber(refunded)}
                </TableCell>

                <TableCell align="right" sx={getCountSx('expired', expired, coverageMeta.level, isInactive)}>
                  {formatNumber(expired)}
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      <Box mt={2} display="flex" justifyContent="center">
        <StandardPagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={safeTotalCount}
          onChangePage={handleChangePage}
          size="small"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default Codes;
